import useApi from 'api';
import Locale from 'data/locale';
import React from 'react';

import Data from 'data';

import Store from 'store';
import EditingContext from 'store/editingContext';
import { ErrorPopup } from 'ui/utils/Overlay';
import { OverlayContext } from 'ui/utils/OverlayManager';
import Dialog from 'ui/dialog';
import { profileFix, profileIsMercenary, profileListItems, stateAddProfile, stateFix, stateIsEmpty, stateIsSingleProfile, stateMergeItems } from 'logic/stateReducers';

const _L = Locale("ui-import");

const OverwriteDialog = ({ onResolve, onClose }) => (
  <Dialog title={_L`Load profile`} modal onClose={onClose}>
    <Dialog.Content>
      <p>{_L`Discard current profile?`}</p>
    </Dialog.Content>
    <Dialog.Footer>
      <button onClick={() => onResolve('merge')}>{_L`Merge`}</button>
      <button onClick={() => onResolve('overwrite')}>{_L`Overwrite`}</button>
      <button onClick={onClose}>{_L`Cancel`}</button>
    </Dialog.Footer>
  </Dialog>
);

const OverwriteMercenaryDialog = ({ onResolve, onClose }) => (
  <Dialog title={_L`Load profile`} modal onClose={onClose}>
    <Dialog.Content>
      <p>{_L`Discard current mercenary?`}</p>
    </Dialog.Content>
    <Dialog.Footer>
      <button onClick={() => onResolve(true)}>{_L`Yes`}</button>
      <button onClick={onClose}>{_L`No`}</button>
    </Dialog.Footer>
  </Dialog>
);

export default function useLoadProfile() {
  const overlays = React.useContext(OverlayContext);
  const setSpinner = EditingContext.useStateSetter("spinner");
  const api = useApi();

  const mainStore = Store.useStore();
  const dispatch = Store.useDispatch();

  return React.useCallback(id => {
    setSpinner(spin => spin + 1);
    api.load(id).then(
      async result => {
        if (!result.profiles || !result.profiles.length) {
          overlays.async(<ErrorPopup message={_L`Empty profile!`}/>);
          return;
        }
        const state = mainStore.getState();

        const isEmpty = stateIsEmpty(state);
        const isSingle = stateIsSingleProfile(result);
        const isMerc = isSingle && profileIsMercenary(result.profiles[0]);

        if (!isSingle || (state.imported && !isMerc) || isEmpty) {
          let action = 'overwrite';
          if (!isEmpty && !(stateIsSingleProfile(state) ? state.profiles[0].imported : state.imported)) {
            action = await overlays.async(<OverwriteDialog/>);
            if (!action) return;
          }
          if (action === 'overwrite') {
            dispatch.setState(stateFix(result, id, result.user));
          } else {
            dispatch.setState(state => {
              state = stateMergeItems(state, result.items, ...result.profiles);
              state.activeProfile = state.profiles.length + result.activeProfile;
              state.profiles = [...state.profiles, ...result.profiles.map(profile => profileFix(profile, state.items))];
              state.buffs = result.buffs;
              state.summons = result.summons;
              return state;
            });
          }
        } else if (isMerc) {
          const profile = state.profiles[state.activeProfile];
          if (!profile.imported && !state.imported && Object.values(profile.mercItems).some(Boolean)) {
            if (!await overlays.async(<OverwriteMercenaryDialog/>)) {
              return;
            }
          }
          dispatch.setState(state => {
            const profile = result.profiles[0];
            const profileItems = {
              mercItems: profile.mercItems,
            };
            state = stateMergeItems(state, result.items, profileItems);
            state.profiles = state.profiles.slice();
            state.profiles[state.activeProfile] = {
              ...state.profiles[state.activeProfile],
              merc: profile.merc,
              mercLevel: profile.mercLevel,
              mercItems: profile.mercItems,
            };
            return state;
          });
        } else {
          dispatch.setState(state => stateAddProfile(state, {...result.profiles[0], name: result.name, imported: true, importedId: id, user: result.user},
            result.items, result.buffs, result.summons));
        }
      },
      error => {
        overlays.async(<ErrorPopup reason={error.message}/>);
      },
    ).finally(() => setSpinner(spin => spin - 1));
  }, [api, overlays, setSpinner, mainStore, dispatch]);
}

// all/null, current, mercenary
export function exportProfile(state, name, type) {
  state = {...state};
  delete state.id;
  if (type === 'current' || type === 'mercenary') {
    state.profiles = [{...state.profiles[state.activeProfile]}];
    state.activeProfile = 0;
    if (type === 'mercenary') {
      state.profiles[0].items = {};
      state.profiles[0].skills = {};
      state.profiles[0].inventory = [];
      state.profiles[0].cube = [];
      delete state.profiles[0].skillProgression;
    }
    if (name) {
      state.profiles[0].name = name;
    }
    const items = {};
    for (const id of profileListItems(state.profiles[0])) {
      const item = items[id] = state.items[id];
      if (item?.socketedItems) {
        for (const id of item.socketedItems) {
          if (state.items[id]) {
            items[id] = state.items[id];
          }
        }
      }
    }
    state.items = items;
  } else {
    state.profiles = state.profiles.map(profile => ({...profile}));
  }
  for (const profile of state.profiles) {
    delete profile.id;
    delete profile.imported;
    delete profile.importedId;
    delete profile.user;
  }
  delete state.imported;
  delete state.importedId;
  if (name != null) {
    state.name = name;
  }

  if (stateIsSingleProfile(state) && profileIsMercenary(state.profiles[0])) {
    const act = Data.hireling[state.profiles[0].merc]?.[0]?.act;
    state.class = act && `act${act}`;
  } else {
    state.class = state.profiles[state.activeProfile].class;
  }

  return state;
}
